// src/components/business/BusinessReposTab.tsx
//
// Aba "Repositórios" da tela do negócio — réplica literal do wireframe
// seção 04 (#bp-repo): header .bizpanel-h ("Repositórios" + contagem .bpcount
// + botão .dnewbtn "Adicionar repositório") e lista .repolist de .repocard
// (ícone .rico, .rinfo nome/URL, .repobr tipo + branches main/dev). Mantém
// editar/remover (wf-iconbtn) e os modais (anatomia WfModal).
//
// C6: dados LAZY — o tab busca os próprios repos quando é montado (a aba abre).

import { useEffect, useState } from 'react';
import { api, type BusinessRepo } from '@/lib/api';
import { BizModal } from './BizModal';
import { WfButton, WfEmpty } from '@/components/wf';
import { Code2, GitBranch, Pencil, Plus, Trash2 } from 'lucide-react';
import { REPO_TYPE_OPTIONS } from './business-constants';

interface BusinessReposTabProps {
  businessId: string;
}

function repoDisplayName(r: BusinessRepo): string {
  if (r.github_owner && r.github_repo) return `${r.github_owner}/${r.github_repo}`;
  return r.github_repo ?? r.label;
}

export function BusinessReposTab({ businessId }: BusinessReposTabProps) {
  const [repos, setRepos] = useState<BusinessRepo[] | null>(null);
  const [listMsg, setListMsg] = useState('');

  // Modal "Adicionar repositório"
  const [showAdd, setShowAdd] = useState(false);
  const [addLabel, setAddLabel] = useState('frontend');
  const [addUrl, setAddUrl] = useState('');
  const [addToken, setAddToken] = useState('');
  const [addDefaultBranch, setAddDefaultBranch] = useState('main');
  const [addPrBranch, setAddPrBranch] = useState('main');
  const [addLoading, setAddLoading] = useState(false);
  const [addMsg, setAddMsg] = useState('');

  // Modal editar repo
  const [editRepo, setEditRepo] = useState<BusinessRepo | null>(null);
  const [editToken, setEditToken] = useState('');
  const [editDefaultBranch, setEditDefaultBranch] = useState('');
  const [editPrBranch, setEditPrBranch] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editMsg, setEditMsg] = useState('');

  // LAZY: busca quando a aba monta.
  useEffect(() => {
    let alive = true;
    api.listBusinessRepos(businessId)
      .then((rps) => { if (alive) setRepos(rps); })
      .catch((err) => {
        if (alive) {
          setRepos([]);
          setListMsg(err instanceof Error ? err.message : 'Erro ao carregar repositórios');
        }
      });
    return () => { alive = false; };
  }, [businessId]);

  const resetAddForm = () => {
    setAddLabel('frontend');
    setAddUrl('');
    setAddToken('');
    setAddDefaultBranch('main');
    setAddPrBranch('main');
    setAddMsg('');
  };

  const handleAdd = async () => {
    if (!addUrl.trim() || !addToken.trim() || !addLabel) return;
    setAddLoading(true);
    setAddMsg('');
    try {
      const repo = await api.addBusinessRepo(businessId, {
        label: addLabel,
        repo_url: addUrl.trim(),
        token: addToken.trim(),
        default_branch: addDefaultBranch.trim() || 'main',
        pr_branch: addPrBranch.trim() || 'main',
      });
      setRepos((prev) => [...(prev ?? []), repo]);
      setShowAdd(false);
      resetAddForm();
    } catch (err) {
      setAddMsg(err instanceof Error ? err.message : 'Erro ao adicionar repositório');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async (repoId: string) => {
    setListMsg('');
    try {
      await api.deleteBusinessRepo(businessId, repoId);
      setRepos((prev) => (prev ?? []).filter((r) => r.id !== repoId));
    } catch (err) {
      setListMsg(err instanceof Error ? err.message : 'Erro ao remover repositório');
    }
  };

  const handleOpenEdit = (r: BusinessRepo) => {
    setEditRepo(r);
    setEditToken('');
    setEditDefaultBranch(r.default_branch);
    setEditPrBranch(r.pr_branch);
    setEditMsg('');
  };

  const handleSaveEdit = async () => {
    if (!editRepo) return;
    setEditLoading(true);
    setEditMsg('');
    try {
      const payload: Partial<{ default_branch: string; pr_branch: string; token: string }> = {
        default_branch: editDefaultBranch.trim() || 'main',
        pr_branch: editPrBranch.trim() || 'main',
      };
      if (editToken.trim()) payload.token = editToken.trim();
      const updated = await api.updateBusinessRepo(businessId, editRepo.id, payload);
      setRepos((prev) => (prev ?? []).map((r) => (r.id === updated.id ? updated : r)));
      setEditRepo(null);
    } catch (err) {
      setEditMsg(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="wf-bizpanel">
      {/* Header (wireframe .bizpanel-h + .bpcount + .dnewbtn) */}
      <div className="wf-bizpanel-h">
        <span>
          Repositórios <span className="wf-bpcount">{repos?.length ?? '…'}</span>
        </span>
        <button type="button" className="wf-dnewbtn" style={{ margin: 0 }} onClick={() => setShowAdd(true)}>
          <Plus className="ic" aria-hidden="true" />
          Adicionar repositório
        </button>
      </div>

      {listMsg && <p className="text-xs mb-2" style={{ color: 'var(--wf-danger-text)' }}>{listMsg}</p>}

      {repos === null ? (
        <div className="wf-repolist" aria-busy="true">
          {[1, 2].map((i) => (
            <div key={i} className="wf-repocard" style={{ minHeight: 56 }} />
          ))}
        </div>
      ) : repos.length === 0 ? (
        <WfEmpty icon={<Code2 />}>
          Nenhum repositório configurado — adicione repositórios para que os
          agentes possam criar PRs automaticamente.
        </WfEmpty>
      ) : (
        <div className="wf-repolist">
          {repos.map((r) => (
            <div key={r.id} className="wf-repocard">
              <div className="rico">
                <Code2 className="ic" aria-hidden="true" />
              </div>
              <div className="rinfo">
                <div className="rname">{repoDisplayName(r)}</div>
                <div className="rurl">
                  {r.github_repo_url ? (
                    <a
                      href={r.github_repo_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {r.github_repo_url.replace(/^https?:\/\//, '')}
                    </a>
                  ) : (
                    'URL não configurada'
                  )}
                </div>
              </div>
              <div className="wf-repobr">
                <span className="wf-rtype">{r.label}</span>
                <span className="wf-br">
                  <GitBranch className="ic" aria-hidden="true" />
                  {r.default_branch}
                </span>
                <span className="wf-br dev">{r.pr_branch}</span>
              </div>
              <div style={{ display: 'flex', gap: 6, flex: '0 0 auto' }}>
                <button
                  type="button"
                  className="wf-iconbtn"
                  aria-label="Editar repositório"
                  title="Editar repositório"
                  onClick={() => handleOpenEdit(r)}
                >
                  <Pencil aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="wf-iconbtn wf-iconbtn--no"
                  aria-label="Remover repositório"
                  title="Remover repositório"
                  onClick={() => void handleDelete(r.id)}
                >
                  <Trash2 aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal: Adicionar repositório */}
      <BizModal
        open={showAdd}
        onClose={() => { setShowAdd(false); setAddMsg(''); }}
        title="Adicionar repositório"
        icon={<Code2 className="ic" aria-hidden="true" />}
        footer={
          <>
            <WfButton variant="ghost" onClick={() => setShowAdd(false)}>Cancelar</WfButton>
            <WfButton
              variant="primary"
              disabled={addLoading || !addLabel || !addUrl.trim() || !addToken.trim()}
              onClick={handleAdd}
            >
              {addLoading ? 'Adicionando…' : 'Adicionar'}
            </WfButton>
          </>
        }
      >
        <div className="wf-field">
          <label className="wf-label" htmlFor="repo-type">Tipo</label>
          <select
            id="repo-type"
            className="wf-select"
            value={addLabel}
            onChange={(e) => setAddLabel(e.target.value)}
          >
            {REPO_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="wf-field">
          <label className="wf-label" htmlFor="repo-url">URL do repositório</label>
          <input
            id="repo-url"
            className="wf-input"
            value={addUrl}
            onChange={(e) => setAddUrl(e.target.value)}
            placeholder="https://github.com/org/repo"
          />
        </div>
        <div className="wf-field">
          <label className="wf-label" htmlFor="repo-token">Personal Access Token</label>
          <input
            id="repo-token"
            className="wf-input"
            type="password"
            value={addToken}
            onChange={(e) => setAddToken(e.target.value)}
            placeholder="ghp_..."
          />
        </div>
        <div className="wf-nc-2col">
          <div className="wf-field">
            <label className="wf-label" htmlFor="repo-main">Branch principal</label>
            <input
              id="repo-main"
              className="wf-input"
              value={addDefaultBranch}
              onChange={(e) => setAddDefaultBranch(e.target.value)}
              placeholder="main"
            />
          </div>
          <div className="wf-field">
            <label className="wf-label" htmlFor="repo-dev">Branch dev</label>
            <input
              id="repo-dev"
              className="wf-input"
              value={addPrBranch}
              onChange={(e) => setAddPrBranch(e.target.value)}
              placeholder="develop"
            />
          </div>
        </div>
        {addMsg && <p className="text-xs" style={{ color: 'var(--wf-danger-text)' }}>{addMsg}</p>}
      </BizModal>

      {/* Modal: Editar repositório */}
      <BizModal
        open={editRepo !== null}
        onClose={() => setEditRepo(null)}
        title={`Editar repositório${editRepo ? ` · ${repoDisplayName(editRepo)}` : ''}`}
        icon={<Pencil className="ic" aria-hidden="true" />}
        footer={
          <>
            <WfButton variant="ghost" onClick={() => setEditRepo(null)}>Cancelar</WfButton>
            <WfButton variant="primary" disabled={editLoading} onClick={handleSaveEdit}>
              {editLoading ? 'Salvando…' : 'Salvar'}
            </WfButton>
          </>
        }
      >
        <div className="wf-field">
          <label className="wf-label" htmlFor="repo-edit-token">Novo Personal Access Token</label>
          <input
            id="repo-edit-token"
            className="wf-input"
            type="password"
            value={editToken}
            onChange={(e) => setEditToken(e.target.value)}
            placeholder="Deixe em branco para manter o atual"
          />
        </div>
        <div className="wf-nc-2col">
          <div className="wf-field">
            <label className="wf-label" htmlFor="repo-edit-main">Branch principal</label>
            <input
              id="repo-edit-main"
              className="wf-input"
              value={editDefaultBranch}
              onChange={(e) => setEditDefaultBranch(e.target.value)}
              placeholder="main"
            />
          </div>
          <div className="wf-field">
            <label className="wf-label" htmlFor="repo-edit-dev">Branch dev</label>
            <input
              id="repo-edit-dev"
              className="wf-input"
              value={editPrBranch}
              onChange={(e) => setEditPrBranch(e.target.value)}
              placeholder="develop"
            />
          </div>
        </div>
        {editMsg && <p className="text-xs" style={{ color: 'var(--wf-danger-text)' }}>{editMsg}</p>}
      </BizModal>
    </div>
  );
}
